import React, { useState, useEffect, useRef } from 'react';
import { 
  Package, CheckCircle2, Clock, User, MapPin, Truck, 
  QrCode, ChevronLeft, ChevronRight, RefreshCw, Search,
  CheckSquare, Square, AlertCircle, Store, Navigation, X, Camera, Scan
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ordersAPI, driversAPI } from '../services/api';
import { toast } from 'sonner';
import CameraBarcodeScanner from '../components/CameraBarcodeScanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PickingPortal = ({ onBack, user }) => {
  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [pickedItems, setPickedItems] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('pending'); // pending, picking, picked
  const [assigning, setAssigning] = useState(false);
  const [showQR, setShowQR] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [allProducts, setAllProducts] = useState([]);

  // Fetch products for barcode lookup
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await fetch(`${API_URL}/api/products`);
        if (res.ok) {
          const data = await res.json();
          setAllProducts(data);
        }
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };
    fetchProducts();
  }, []);

  // Fetch orders that need picking
  const fetchOrders = async () => {
    try {
      setLoading(true);
      const data = await ordersAPI.getAll();
      // Filter orders that need picking (payment confirmed or picking in progress)
      const pickableOrders = data.filter(order => 
        ['payment_confirmed', 'picking', 'picked'].includes(order.status)
      );
      setOrders(pickableOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  // Fetch available drivers
  const fetchDrivers = async () => {
    try {
      const data = await driversAPI.getAll();
      // Filter active drivers
      setDrivers(data.filter(d => d.status === 'active' || d.status === 'available'));
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  useEffect(() => {
    fetchOrders();
    fetchDrivers();
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchOrders();
      fetchDrivers();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Start picking an order
  const startPicking = async (order) => {
    try {
      await ordersAPI.updateStatus(order.id, 'picking');
      setSelectedOrder(order);
      // Initialize picked items state
      const initialPicked = {};
      order.items.forEach((item, idx) => {
        initialPicked[idx] = false;
      });
      setPickedItems(initialPicked);
      toast.success(`Started picking order #${order.order_number}`);
      fetchOrders();
    } catch (error) {
      toast.error('Failed to start picking');
    }
  };

  // Toggle item picked status
  const toggleItemPicked = (itemIndex) => {
    setPickedItems(prev => ({
      ...prev,
      [itemIndex]: !prev[itemIndex]
    }));
  };

  // Check if all items are picked
  const allItemsPicked = () => {
    if (!selectedOrder) return false;
    return selectedOrder.items.every((_, idx) => pickedItems[idx]);
  };

  // Complete picking and assign to driver or mark for pickup
  const completePicking = async (assignToDriver = true, driverId = null) => {
    if (!allItemsPicked()) {
      toast.error('Please pick all items before completing');
      return;
    }

    try {
      setAssigning(true);
      
      // Mark order as picked
      await ordersAPI.updateStatus(selectedOrder.id, 'picked');
      
      if (assignToDriver && driverId) {
        // Assign to driver
        await ordersAPI.assignDriver(selectedOrder.id, driverId);
        toast.success('Order picked and assigned to driver!');
      } else if (selectedOrder.order_type === 'pickup') {
        // Mark as ready for pickup
        await ordersAPI.updateStatus(selectedOrder.id, 'ready_for_pickup');
        toast.success('Order ready for customer pickup!');
      } else {
        toast.success('Order picked! Waiting for driver assignment.');
      }
      
      setSelectedOrder(null);
      setPickedItems({});
      fetchOrders();
    } catch (error) {
      toast.error('Failed to complete picking');
    } finally {
      setAssigning(false);
    }
  };

  // Filter orders based on search and filter
  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.order_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.delivery_info?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (filter === 'pending') return order.status === 'payment_confirmed' && matchesSearch;
    if (filter === 'picking') return order.status === 'picking' && matchesSearch;
    if (filter === 'picked') return order.status === 'picked' && matchesSearch;
    return matchesSearch;
  });

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#0a1628]" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="picking-portal">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onBack} className="p-2">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#0a1628] flex items-center gap-2">
              <Package className="w-7 h-7" />
              Picking Portal
            </h1>
            <p className="text-gray-500">Pick and pack orders for delivery</p>
          </div>
        </div>
        <Button onClick={fetchOrders} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by order number or customer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          {[
            { id: 'pending', label: 'To Pick', count: orders.filter(o => o.status === 'payment_confirmed').length },
            { id: 'picking', label: 'In Progress', count: orders.filter(o => o.status === 'picking').length },
            { id: 'picked', label: 'Picked', count: orders.filter(o => o.status === 'picked').length },
          ].map(f => (
            <Button
              key={f.id}
              variant={filter === f.id ? 'default' : 'outline'}
              onClick={() => setFilter(f.id)}
              className={filter === f.id ? 'bg-[#0a1628]' : ''}
            >
              {f.label}
              <Badge className="ml-2 bg-white/20">{f.count}</Badge>
            </Button>
          ))}
        </div>
      </div>

      {/* Selected Order - Picking View */}
      {selectedOrder && (
        <Card className="mb-6 border-2 border-[#0a1628]">
          <div className="p-4 bg-[#0a1628] text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">Picking Order #{selectedOrder.order_number}</h2>
                <p className="text-white/80">{selectedOrder.delivery_info?.name}</p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  className="text-white hover:bg-white/20"
                  onClick={() => setShowQR(selectedOrder)}
                >
                  <QrCode className="w-5 h-5 mr-2" />
                  View QR
                </Button>
                <Button
                  variant="ghost"
                  className="text-white hover:bg-white/20"
                  onClick={() => {
                    setSelectedOrder(null);
                    setPickedItems({});
                  }}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
          
          <div className="p-4">
            {/* Items to Pick */}
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2">
                <Package className="w-5 h-5 text-[#0a1628]" />
                Items to Pick ({Object.values(pickedItems).filter(Boolean).length}/{selectedOrder.items.length})
              </h3>
              <Button
                onClick={() => setShowScanner(true)}
                variant="outline"
                className="gap-2 bg-blue-50 text-blue-700 hover:bg-blue-100"
                data-testid="picking-scan-btn"
              >
                <Camera className="w-4 h-4" />
                Scan to Pick
              </Button>
            </div>
            
            <div className="space-y-2 mb-6">
              {selectedOrder.items.map((item, idx) => (
                <div
                  key={idx}
                  onClick={() => toggleItemPicked(idx)}
                  className={`flex items-center gap-4 p-3 rounded-lg cursor-pointer transition-all ${
                    pickedItems[idx] 
                      ? 'bg-green-50 border-2 border-green-500' 
                      : 'bg-gray-50 border-2 border-transparent hover:border-gray-300'
                  }`}
                >
                  {pickedItems[idx] ? (
                    <CheckSquare className="w-6 h-6 text-green-500 flex-shrink-0" />
                  ) : (
                    <Square className="w-6 h-6 text-gray-400 flex-shrink-0" />
                  )}
                  
                  {item.image && (
                    <img 
                      src={item.image} 
                      alt={item.name}
                      className="w-12 h-12 rounded object-cover"
                    />
                  )}
                  
                  <div className="flex-1">
                    <p className={`font-medium ${pickedItems[idx] ? 'line-through text-gray-500' : ''}`}>
                      {item.name}
                    </p>
                    <p className="text-sm text-gray-500">
                      Qty: {item.quantity} × ${item.price?.toFixed(2)}
                    </p>
                  </div>
                  
                  {pickedItems[idx] && (
                    <Badge className="bg-green-500">Picked</Badge>
                  )}
                </div>
              ))}
            </div>

            {/* Complete Picking */}
            {allItemsPicked() && (
              <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-4 text-green-600">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-semibold">All items picked!</span>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {selectedOrder.order_type === 'pickup' ? (
                    <Button
                      onClick={() => completePicking(false)}
                      disabled={assigning}
                      className="bg-blue-500 hover:bg-blue-600 gap-2"
                    >
                      <Store className="w-5 h-5" />
                      Mark Ready for Pickup
                    </Button>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-medium mb-2">Assign to Driver</label>
                        <select
                          className="w-full p-2 border rounded-lg"
                          onChange={(e) => {
                            if (e.target.value) {
                              completePicking(true, e.target.value);
                            }
                          }}
                          disabled={assigning}
                        >
                          <option value="">Select a driver...</option>
                          {drivers.map(driver => (
                            <option key={driver.id} value={driver.id}>
                              {driver.first_name} {driver.last_name} - {driver.status}
                            </option>
                          ))}
                        </select>
                      </div>
                      <Button
                        onClick={() => completePicking(false)}
                        disabled={assigning}
                        variant="outline"
                        className="gap-2 h-[42px] mt-6"
                      >
                        <Clock className="w-5 h-5" />
                        Keep in Store (Assign Later)
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Orders List */}
      <div className="grid gap-4">
        {filteredOrders.length === 0 ? (
          <Card className="p-8 text-center">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No orders to pick</p>
          </Card>
        ) : (
          filteredOrders.map(order => (
            <Card key={order.id} className="p-4 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex gap-4">
                  {/* QR Code Preview */}
                  <div 
                    className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-200"
                    onClick={() => setShowQR(order)}
                  >
                    {order.qr_code ? (
                      <img src={order.qr_code} alt="QR Code" className="w-full h-full object-contain" />
                    ) : (
                      <QrCode className="w-8 h-8 text-gray-400" />
                    )}
                  </div>
                  
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-lg">#{order.order_number}</h3>
                      <Badge className={
                        order.status === 'payment_confirmed' ? 'bg-yellow-500' :
                        order.status === 'picking' ? 'bg-blue-500' :
                        order.status === 'picked' ? 'bg-green-500' : 'bg-gray-500'
                      }>
                        {order.status === 'payment_confirmed' ? 'Ready to Pick' :
                         order.status === 'picking' ? 'Picking...' :
                         order.status === 'picked' ? 'Picked' : order.status}
                      </Badge>
                      {order.order_type === 'pickup' && (
                        <Badge variant="outline" className="border-blue-500 text-blue-500">
                          <Store className="w-3 h-3 mr-1" />
                          Store Pickup
                        </Badge>
                      )}
                    </div>
                    
                    <div className="text-sm text-gray-600 space-y-1">
                      <p className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {order.delivery_info?.name}
                      </p>
                      <p className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {order.delivery_info?.address}, {order.delivery_info?.city}
                      </p>
                      <p className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDate(order.created_at)}
                      </p>
                    </div>
                    
                    <div className="mt-2">
                      <p className="text-sm font-medium">
                        {order.items?.length} items • ${order.total?.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-col gap-2">
                  {order.status === 'payment_confirmed' && (
                    <Button
                      onClick={() => startPicking(order)}
                      className="bg-[#0a1628] gap-2"
                    >
                      <Package className="w-4 h-4" />
                      Start Picking
                    </Button>
                  )}
                  {order.status === 'picking' && (
                    <Button
                      onClick={() => {
                        setSelectedOrder(order);
                        const initialPicked = {};
                        order.items.forEach((_, idx) => {
                          initialPicked[idx] = false;
                        });
                        setPickedItems(initialPicked);
                      }}
                      className="bg-blue-500 gap-2"
                    >
                      <Package className="w-4 h-4" />
                      Continue Picking
                    </Button>
                  )}
                  {order.status === 'picked' && !order.assigned_driver_id && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSelectedOrder(order);
                        const initialPicked = {};
                        order.items.forEach((_, idx) => {
                          initialPicked[idx] = true;
                        });
                        setPickedItems(initialPicked);
                      }}
                      className="gap-2"
                    >
                      <Truck className="w-4 h-4" />
                      Assign Driver
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowQR(order)}
                    className="text-gray-500"
                  >
                    <QrCode className="w-4 h-4 mr-1" />
                    View QR
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* QR Code Modal */}
      {showQR && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">Order QR Code</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowQR(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="text-center">
              <p className="text-lg font-semibold mb-2">#{showQR.order_number}</p>
              
              {showQR.qr_code ? (
                <img 
                  src={showQR.qr_code} 
                  alt="Order QR Code" 
                  className="w-64 h-64 mx-auto border-4 border-[#0a1628] rounded-lg"
                />
              ) : (
                <div className="w-64 h-64 mx-auto bg-gray-100 rounded-lg flex items-center justify-center">
                  <QrCode className="w-16 h-16 text-gray-400" />
                </div>
              )}
              
              <p className="text-sm text-gray-500 mt-4">
                Driver scans this QR code to start delivery
              </p>
              
              <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm">
                <p><strong>Customer:</strong> {showQR.delivery_info?.name}</p>
                <p><strong>Items:</strong> {showQR.items?.length} items</p>
                <p><strong>Total:</strong> ${showQR.total?.toFixed(2)}</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Camera Barcode Scanner for Picking Items */}
      <CameraBarcodeScanner
        isOpen={showScanner && selectedOrder !== null}
        onClose={() => setShowScanner(false)}
        onScan={(barcode, foundProduct) => {
          // Clean the barcode
          let cleanBarcode = barcode;
          if (barcode.startsWith('UPLANDS-PRODUCT:')) {
            cleanBarcode = barcode.replace('UPLANDS-PRODUCT:', '');
          }
          
          // Find matching item in order - try multiple matching strategies
          let itemIndex = -1;
          
          // First try: match by barcode directly in order items
          itemIndex = selectedOrder.items.findIndex(item => 
            item.barcode === cleanBarcode
          );
          
          // Second try: match by found product from scanner
          if (itemIndex === -1 && foundProduct) {
            itemIndex = selectedOrder.items.findIndex(item => 
              item.name?.toLowerCase() === foundProduct.name?.toLowerCase() ||
              item.id === foundProduct.id
            );
          }
          
          // Third try: match by barcode in all products then find in order
          if (itemIndex === -1) {
            const product = allProducts.find(p => 
              p.barcode === cleanBarcode || 
              p.id === cleanBarcode
            );
            if (product) {
              itemIndex = selectedOrder.items.findIndex(item => 
                item.name?.toLowerCase() === product.name?.toLowerCase() ||
                item.id === product.id
              );
            }
          }
          
          if (itemIndex === -1) {
            const itemName = foundProduct?.name || cleanBarcode;
            toast.error(`"${itemName}" is not in this order`);
            return;
          }
          
          const itemName = selectedOrder.items[itemIndex].name;
          
          if (pickedItems[itemIndex]) {
            toast.info(`${itemName} already picked`);
          } else {
            // Mark item as picked
            setPickedItems(prev => ({
              ...prev,
              [itemIndex]: true
            }));
            toast.success(`✓ Picked: ${itemName}`);
          }
          
          // Check if all items are now picked
          const newPickedItems = { ...pickedItems, [itemIndex]: true };
          const allPicked = selectedOrder.items.every((_, idx) => newPickedItems[idx]);
          
          if (allPicked) {
            toast.success('🎉 All items picked! Ready to complete order.');
            setShowScanner(false);
          }
        }}
        products={allProducts}
        onAddToCart={(product) => {
          // In picking mode, "add to cart" means mark as picked
          let itemIndex = selectedOrder.items.findIndex(item => 
            item.name?.toLowerCase() === product.name?.toLowerCase() ||
            item.id === product.id ||
            item.barcode === product.barcode
          );
          
          if (itemIndex !== -1 && !pickedItems[itemIndex]) {
            setPickedItems(prev => ({
              ...prev,
              [itemIndex]: true
            }));
            toast.success(`✓ Picked: ${product.name}`);
            
            // Check if all items are now picked
            const newPickedItems = { ...pickedItems, [itemIndex]: true };
            const allPicked = selectedOrder.items.every((_, idx) => newPickedItems[idx]);
            
            if (allPicked) {
              toast.success('🎉 All items picked! Ready to complete order.');
              setShowScanner(false);
            }
          }
        }}
        mode="cart"
      />
    </div>
  );
};

export default PickingPortal;
